import { FC, ReactNode } from 'react';

interface ScenarioLayoutComponentProps {
  /** Flag to determine if we're in fullscreen mode */
  isFullScreenMode: boolean;
  /** Scenario header section (only shown in normal mode) */
  scenarioHeaderSection: ReactNode;
  /** Title section with scenario name and info */
  titleSection: ReactNode;
  /** Tabs section (shown in fullscreen mode) */
  tabsSection: ReactNode;
  /** Filter section with tabs (in fullscreen) and filters */
  filterSection: ReactNode;
  /** Action buttons section */
  actionButtonsSection: ReactNode;
  /** Scenario table content */
  scenarioTableContent: ReactNode;
  /** Summary table content */
  summaryTableContent: ReactNode;
  /** Currently active tab in fullscreen mode */
  activeTab: 'scenario' | 'summary';
  /** Optional CSS class for the container */
  className?: string;
}

/**
 * ScenarioLayoutComponent - Handles the rendering logic for scenario planner
 *
 * This component is responsible only for deciding what to show based on the isFullScreenMode flag.
 * All logic, state management, and calculations remain in the parent component.
 *
 * @param props - The layout props containing all sections and the mode flag
 * @returns The rendered layout based on the current mode
 */

const ScenarioLayoutManagerComponent: FC<ScenarioLayoutComponentProps> = ({
  isFullScreenMode,
  scenarioHeaderSection,
  titleSection,
  tabsSection,
  filterSection,
  actionButtonsSection,
  scenarioTableContent,
  summaryTableContent,
  activeTab,
  className = '',
}) => {
  /**
   * Renders the header content based on the current mode
   */
  const renderHeaderContent = () => {
    return (
      <div
        id="header-container"
        className={`header-container ${isFullScreenMode ? 'fullscreen-mode' : ''}`}
      >
        {!isFullScreenMode && titleSection}

        <div className="breadcrumbs-container">
          <div className="breadcrumbs">
            <div className={`${!isFullScreenMode ? 'd-none' : ''}`}>
              {tabsSection}
            </div>
            <div className={`${isFullScreenMode ? '' : 'breadcrumbs-left'}`}>
              {filterSection}
            </div>
            {actionButtonsSection}
          </div>
        </div>
      </div>
    );
  };

  /**
   * Renders the main content based on the current mode
   */
  const renderMainContent = () => {
    return (
      <div
        className={`sceanrio-details-content ${isFullScreenMode ? 'fullscreen-content' : 'normal-content'}`}
      >
        <div
          className={`${activeTab === 'scenario' || !isFullScreenMode ? '' : 'd-none'}`}
        >
          {scenarioTableContent}
        </div>
        <div
          className={`${activeTab === 'summary' || !isFullScreenMode ? '' : 'd-none'}`}
        >
          {summaryTableContent}
        </div>
      </div>
    );
  };

  return (
    <div className={`scenario-header-container ${className}`}>
      {!isFullScreenMode && scenarioHeaderSection}
      {renderHeaderContent()}
      {renderMainContent()}
    </div>
  );
};

export default ScenarioLayoutManagerComponent;
